import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CmsSsrComponentProps, ICms } from '@/types/cms';
import Container from './Container';
import { parseJson } from '@/utils/parseJson';
import { CaratDown } from '@weee-fe/kunlunicon';
import { getConfigBg } from '../tools/config-bg';
import ImpTracker from '@/components/common/ImpTracker';
import useImpressionT2 from '@/tracks/useImpressionT2';
import useClickActionT2 from '@/tracks/useClickActionT2';
import useTranslation from 'next-translate/useTranslation';

const Navigation: React.FC<
  CmsSsrComponentProps & { sections: ICms['layout']['sections']; allStaticRes: { [key: string]: unknown } }
> = props => {
  const {
    componentKey,
    componentInstanceKey,
    properties,
    sections,
    componentPosition,
    trackingContext,
    top,
    allStaticRes
  } = props;
  const { t } = useTranslation('common');
  const [activeSection, setActiveSection] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [stickyStatus, setStickyStatus] = useState<boolean>();
  const [dropdownDirection, setDropdownDirection] = useState<'down' | 'up'>('down');
  const bgStyle = getConfigBg(properties?.background);
  const stickyWatcherRef = useRef<HTMLDivElement>(null);
  const navContainerRef = useRef<HTMLDivElement>(null);
  const { filterBtnImpression } = useImpressionT2();
  const { commonClick } = useClickActionT2();

  const initialNavItems = useMemo(() => {
    const items: { key: string; label: string }[] = parseJson(properties.nav_item);
    const keys = extractComponentInstanceKeys(sections);
    return items.filter(i => {
      if (!keys.includes(i.key)) return false;

      const staticData = allStaticRes?.[i.key] as { object?: { products?: unknown[] } } | undefined;
      if (staticData?.object?.products !== undefined) {
        return Array.isArray(staticData.object.products) && staticData.object.products.length > 0;
      }
      return true;
    });
  }, [properties.nav_item, sections, allStaticRes]);

  const [navItems, setNavItems] = useState<{ key: string; label: string }[]>(initialNavItems);
  const [fadingItems, setFadingItems] = useState<Set<string>>(new Set());

  // 监听cms-activity-root元素，当navItem对应的DOM被移除时，触发淡出动画后移除对应项
  useEffect(() => {
    if (navItems.length === 0) return;

    const root = document.getElementById('cms-activity-root');
    if (!root) return;

    const observer = new MutationObserver(() => {
      // 找出需要移除的items（DOM已消失且不在fading状态）
      const itemsToFade = navItems.filter(item => {
        const element = document.getElementById(item.key);
        return element === null && !fadingItems.has(item.key);
      });

      if (itemsToFade.length > 0) {
        // 先标记为fading状态，触发动画
        setFadingItems(prev => {
          const newSet = new Set(prev);
          itemsToFade.forEach(item => newSet.add(item.key));
          return newSet;
        });

        // 动画结束后（300ms）真正移除
        setTimeout(() => {
          const keysToRemove = itemsToFade.map(item => item.key);
          setNavItems(prev => prev.filter(item => !keysToRemove.includes(item.key)));
          setFadingItems(prev => {
            const newSet = new Set(prev);
            keysToRemove.forEach(key => newSet.delete(key));
            return newSet;
          });
        }, 300);
      }
    });

    observer.observe(root, {
      childList: true,
      subtree: false
    });

    return () => {
      observer.disconnect();
    };
  }, [navItems, fadingItems]);

  // 更新URL参数 - 完全避免操作history，只更新地址栏显示
  // 备选方案 - 直接修改浏览器历史状态
  const updateUrlParams = useCallback((itemKey: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('navbar', itemKey);

    // 直接修改当前历史记录条目的URL，不触发任何路由事件
    window.history.replaceState(
      {
        ...window.history.state,
        options: { ...window.history.state?.options, __no_replace: 1 }
      },
      '',
      url.toString()
    );
  }, []);

  // 滚动到指定section
  const scrollToSection = useCallback((sectionKey: string) => {
    const element = document.getElementById(sectionKey);
    if (element) {
      element.scrollIntoView();
    }
  }, []);

  function extractComponentInstanceKeys(sections: ICms['layout']['sections']) {
    const keys = [];
    sections.forEach(section => {
      if (section.components && Array.isArray(section.components)) {
        section.components.forEach(component => {
          if (component.component_instance_key) {
            keys.push(component.component_instance_key);
          }
        });
      }
    });
    return keys;
  }

  useEffect(() => {
    if (!stickyWatcherRef.current) return;

    const observer = new IntersectionObserver(
      ([e]) => {
        setStickyStatus(!e.isIntersecting);
        if (e.isIntersecting && navItems.length > 0) setActiveSection(navItems[0].key);
      },
      { threshold: [1], rootMargin: `-${top + 1}px 0px 0px 0px` }
    );

    observer.observe(stickyWatcherRef.current);

    return () => {
      observer.disconnect();
    };
  }, [top]);

  // 导航按钮居中滚动
  useEffect(() => {
    if (!activeSection) return;

    const timer = setTimeout(() => {
      const activeButton = document.querySelector(`[data-nav-id="${activeSection}"]`);
      const container = document.getElementById('scroll-container');

      if (activeButton && container) {
        const buttonElement = activeButton as HTMLElement;
        const scrollLeft = buttonElement.offsetLeft - (container.clientWidth - buttonElement.clientWidth) / 2;
        container.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [activeSection]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const navbarParam = urlParams.get('navbar');
    if (navbarParam && navItems.some(item => item.key === navbarParam)) {
      // 如果URL中有navbar参数且对应的item存在，设置为激活状态
      setTimeout(() => {
        scrollToSection(navbarParam);
      }, 1000);
    }
  }, []);

  // 优化的滚动检测逻辑
  useEffect(() => {
    if (navItems.length === 0) return;

    let ticking = false;

    const findActiveSection = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      // 触发点：当前滚动位置 + 导航栏高度 + 偏移量
      const triggerPoint = scrollTop + (top || 0) + 100;

      let activeKey = '';

      // 从前往后遍历，找到最后一个进入视口的section
      for (let i = 0; i < navItems.length; i++) {
        const item = navItems[i];
        const element = document.getElementById(item.key);
        if (!element) continue;

        const elementTop = element.offsetTop;

        // 如果元素顶部已经经过了触发点，则认为这个section是激活的
        if (elementTop <= triggerPoint) {
          activeKey = item.key;
        } else {
          // 如果当前元素还没到触发点，后面的也不会到，可以停止遍历
          break;
        }
      }

      // 如果没有找到激活的section（页面在最顶部），默认激活第一个
      if (!activeKey && navItems.length > 0) {
        activeKey = navItems[0].key;
      }

      // 只在activeSection发生变化时才更新
      if (activeKey && activeKey !== activeSection) {
        setActiveSection(activeKey);
      }
    };

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          findActiveSection();
          ticking = false;
        });
        ticking = true;
      }
    };

    if (isDropdownOpen) {
      window.removeEventListener('scroll', handleScroll);
      return;
    }

    // 添加滚动监听器
    window.addEventListener('scroll', handleScroll, { passive: true });

    // 初始检查
    findActiveSection();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [navItems, activeSection, top, scrollToSection, isDropdownOpen]);

  const lockBody = () => {
    // Store current scroll position
    const scrollY = window.scrollY;
    // Add styles to prevent scrolling
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
  };

  const unlockBody = () => {
    const scrollY = document.body.style.top;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, parseInt(scrollY || '0') * -1);
  };

  // 计算dropdown方向的函数
  const calculateDropdownDirection = useCallback(() => {
    if (!navContainerRef.current) return 'down';

    const navRect = navContainerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 288; // max-h-72 = 18rem = 288px

    const spaceBelow = viewportHeight - navRect.bottom;
    const spaceAbove = navRect.top;

    // 如果下方空间足够，向下打开；否则检查上方空间
    if (spaceBelow >= dropdownMaxHeight) {
      return 'down';
    } else if (spaceAbove >= dropdownMaxHeight) {
      return 'up';
    } else {
      // 两边空间都不够时，选择空间更大的一边
      return spaceBelow > spaceAbove ? 'down' : 'up';
    }
  }, []);

  // 更新dropdown打开逻辑
  const toggleDropdown = useCallback(() => {
    if (isDropdownOpen) {
      unlockBody();
      setIsDropdownOpen(false);
    } else {
      lockBody();
      const direction = calculateDropdownDirection();
      setDropdownDirection(direction);
      setIsDropdownOpen(true);
    }
  }, [isDropdownOpen, calculateDropdownDirection]);

  if (!navItems) return <></>;

  return (
    <>
      {/* Overlay Mask */}
      {isDropdownOpen && (
        <div
          className={`${stickyStatus ? 'z-[301]' : 'z-[302]'} fixed inset-0 bg-black/50`}
          onClick={() => {
            setIsDropdownOpen(false);
            unlockBody();
          }}
        />
      )}
      <Container
        componentKey={componentKey}
        componentInstanceKey={componentInstanceKey}
        properties={properties}
        className={`${!stickyStatus && !isDropdownOpen ? 'z-[209]' : 'z-[302]'} sticky`}
        style={{
          top: top
        }}>
        <nav
          className={`${stickyStatus ? '' : 'py-3 px-4'} bg-[#ffffff] transition-colors duration-300`}
          style={{ ...bgStyle }}>
          <div
            ref={navContainerRef}
            className={`${
              stickyStatus ? 'border-b border-t' : 'border rounded-lg'
            } border-solid border-surface-100-hairline relative mx-auto w-full flex box-border items-center bg-[#ffffff] px-2 py-1 z-20 `}
            style={{
              boxShadow: isDropdownOpen ? '0px 2px 6px 0px #00000026' : 'unset',
              overflow: isDropdownOpen ? '' : 'hidden'
            }}>
            {/* Horizontal Scroll */}
            <div
              id="scroll-container"
              className={`overflow-x-auto flex scroll-smooth ${isDropdownOpen ? 'hidden' : ''}`}
              style={{
                width: 'calc(100% - 32px)'
              }}>
              {navItems.map((category, index) => (
                <button
                  key={category.key}
                  data-nav-id={category.key}
                  aria-label={category.label}
                  data-id={category.key}
                  onClick={() => {
                    commonClick({
                      mod_nm: properties?.event_key || componentKey,
                      mod_pos: componentPosition,
                      co: {
                        target_nm: category.label,
                        target_pos: index,
                        target_type: 'filter_button',
                        click_type: 'view'
                      },
                      ctx: trackingContext
                    });
                    updateUrlParams(category.key);
                    scrollToSection(category.key);
                  }}
                  className={`${
                    activeSection === category.key
                      ? 'text-navbar-logo border-0 border-b-2 border-solid border-navbar-logo pb-1.5'
                      : 'text-surface-100-fg-minor pb-2'
                  } flex-none px-3 pt-2 enki-body-xs-medium transition-all duration-300 whitespace-nowrap bg-[#ffffff]`}
                  style={{
                    opacity: fadingItems.has(category.key) ? 0 : 1,
                    transform: fadingItems.has(category.key) ? 'scale(0.8)' : 'scale(1)',
                    maxWidth: fadingItems.has(category.key) ? 0 : '200px',
                    padding: fadingItems.has(category.key) ? '0' : undefined,
                    overflow: 'hidden'
                  }}>
                  <ImpTracker
                    impTrackingT2={() => {
                      filterBtnImpression({
                        mod_nm: properties?.event_key || componentKey,
                        mod_pos: componentPosition,
                        co: {
                          button_nm: category.label,
                          button_pos: index
                        },
                        ctx: trackingContext
                      });
                    }}>
                    {category.label}
                  </ImpTracker>
                </button>
              ))}
            </div>
            {/* Dropdown Button */}
            <div
              onClick={toggleDropdown}
              className="absolute right-0 top-0 bottom-0 flex items-center justify-center w-8"
              style={{
                boxShadow: '-3px 0px 8px 0px #00000026'
              }}>
              <CaratDown
                className={`w-5 h-5 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>
            {/* Choose Category Button (Only shown when dropdown is open) */}
            {isDropdownOpen && (
              <div
                onClick={() => {
                  setIsDropdownOpen(false);
                  unlockBody();
                }}
                className="flex-1 px-3 py-2 enki-body-xs-medium text-btn-disabled-fg-default">
                {t('cms_jump_to_section')}
              </div>
            )}
            {/* Dropdown Menu */}
          </div>
          <div
            className={`${stickyStatus ? 'left-0 w-full' : 'left-4 w-[calc(100%-32px)]'} absolute ${
              dropdownDirection === 'up' ? 'bottom-10' : 'top-10'
            } z-10  transition-transform duration-300 ease-out
                  ${
                    isDropdownOpen
                      ? 'translate-y-0'
                      : dropdownDirection === 'up'
                      ? 'translate-y-2 hidden'
                      : '-translate-y-2 hidden'
                  }`}>
            <div
              className={`${stickyStatus ? '' : 'rounded-lg'} bg-[#ffffff] enki-elevation-5 border p-2 ${
                dropdownDirection === 'up' ? 'pb-5' : 'pt-5'
              } flex flex-wrap gap-2 max-h-72 overflow-y-auto`}>
              {navItems.map((category, index) => (
                <button
                  key={category.key}
                  data-nav-id={category.key}
                  aria-label={category.label}
                  data-id={category.key}
                  onClick={() => {
                    commonClick({
                      mod_nm: properties?.event_key || componentKey,
                      mod_pos: componentPosition,
                      co: {
                        target_nm: category.label,
                        target_pos: index,
                        target_type: 'filter_button',
                        click_type: 'view'
                      },
                      ctx: trackingContext
                    });
                    setIsDropdownOpen(false);
                    unlockBody();
                    updateUrlParams(category.key);
                    scrollToSection(category.key);
                  }}
                  className={`${
                    activeSection === category.key
                      ? 'text-navbar-logo border-0 border-b-2 border-solid border-navbar-logo pb-1.5'
                      : 'text-surface-100-fg-minor pb-2'
                  } px-3 py-2 enki-body-xs-medium transition-all duration-300 whitespace-nowrap bg-[#ffffff]`}
                  style={{
                    opacity: fadingItems.has(category.key) ? 0 : 1,
                    transform: fadingItems.has(category.key) ? 'scale(0.8)' : 'scale(1)'
                  }}>
                  <ImpTracker
                    impTrackingT2={() => {
                      filterBtnImpression({
                        mod_nm: properties?.event_key || componentKey,
                        mod_pos: componentPosition,
                        co: {
                          button_nm: category.label,
                          button_pos: index
                        },
                        ctx: trackingContext
                      });
                    }}>
                    {category.label}
                  </ImpTracker>
                </button>
              ))}
            </div>
          </div>
        </nav>
      </Container>
      <div ref={stickyWatcherRef} />
    </>
  );
};

export default Navigation;
